import { useEffect, useMemo } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { useCmsClientContext } from '../../utils/cmsPageContext/useCmsClientContext';
import { BrandId } from '../../utils/override';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { FeLink } from '../FeLink';
import { config } from './config';
import {
  genderOptions,
  maritalStatus,
  occupationType,
  yesNoOptions,
  drivingExperience,
  bolttechDrivingExperience,
  numberOfClaims,
  bolttechNumberOfClaims,
} from './constants';
import { useCarInsuranceContext } from './context';
import { FormSelect } from './Form/FormSelect';
import { RadioButtonGroup } from './Inputs/RadioGroup';
import { TabsCheckbox } from './Inputs/TabsCheckbox';
import { localizeForm } from './localizeEditQuoteSidebar';

type PersonalDetailsFormProps = {
  useBolttechData: boolean;
  pushDataLayer: PushDataLayerFn;
};

export const PersonalDetailsForm: React.FC<PersonalDetailsFormProps> = ({
  useBolttechData,
  pushDataLayer,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  const { lang, brand } = context;
  const localization = localizeForm(lang, brand as BrandId);

  const translatedGenderOptions = genderOptions(staticText);
  const translatedMaritalStatOptions = maritalStatus(staticText);
  const translatedOccupationType = occupationType(staticText);
  const translatedyesNoOptions = yesNoOptions(staticText);
  const translatedDrivingExperience = drivingExperience(staticText);
  const translatedBolttechDrivingExperience =
    bolttechDrivingExperience(staticText);

  const {
    requestParams: { driver, ncd, offPeakVehicle, offenceFree },
  } = useCarInsuranceContext();
  const { ncdOptions } = config()[brand as BrandId];

  const showOffenceFreeField = useMemo(() => {
    return brand === 'singsaver' && useBolttechData && ncd.value >= 30;
  }, [brand, useBolttechData, ncd.value]);

  const handleAgeChange = (value: string) => {
    pushDataLayer({
      event: 'form_interact',
      component_type: 'dropdown',
      component_title: 'Age',
      action: 'change',
      form_field_value: value,
      lead_form_name: 'ci-policy-search-module-driver-info',
      vertical_code: 'CI',
    });

    driver.setValue({ age: Number(value) });
  };

  const handleGenderChange = (value: string) => {
    pushDataLayer({
      event: 'form_interact',
      component_type: 'tabs_checkbox',
      component_title: 'Gender',
      action: 'change',
      form_field_value: value,
      lead_form_name: 'ci-policy-search-module-driver-info',
      vertical_code: 'CI',
    });

    driver.setValue({
      gender: value,
    });
  };

  const handleMaritalStatusChange = (value: string) => {
    pushDataLayer({
      event: 'form_interact',
      component_type: 'button',
      component_title: 'Marital Status',
      action: 'change',
      form_field_value: value === 'S' ? 'Single' : 'Married',
      lead_form_name: 'ci-policy-search-module-driver-info',
      vertical_code: 'CI',
    });

    driver.setValue({
      maritalStatus: value,
    });
  };

  const handleDrivingExperienceChange = (value: string) => {
    pushDataLayer({
      event: 'form_interact',
      component_type: 'dropdown',
      component_title: 'Driving Experience',
      action: 'change',
      form_field_value: value,
      lead_form_name: 'ci-policy-search-module-driver-info',
      vertical_code: 'CI',
    });

    driver.setValue({
      totalDrivingExperience: Number(value),
    });
  };

  const handleNCDChange = (value: string) => {
    pushDataLayer({
      event: 'form_interact',
      component_type: 'dropdown',
      component_title: 'NCD',
      action: 'change',
      form_field_value: value,
      lead_form_name: 'ci-policy-search-module-driver-info',
      vertical_code: 'CI',
    });

    ncd.setValue(Number(value));
  };

  const handleNoOfClaimsChange = (value: string) => {
    pushDataLayer({
      event: 'form_interact',
      component_type: 'dropdown',
      component_title: 'Number of claims in the last 3 years',
      action: 'change',
      form_field_value: value,
      lead_form_name: 'ci-policy-search-module-driver-info',
      vertical_code: 'CI',
    });

    driver.setValue({
      numberOfClaims: Number(value),
    });
  };

  const handleOccupationTypeChange = (value: string) => {
    driver.setValue({
      occupationType: value,
    });
  };

  const handleOffPeakVehicleChange = (value: string) => {
    offPeakVehicle.setValue(value);
  };

  const handleOffenceFreeChange = (value: string) => {
    offenceFree.setValue(value);
  };

  // The question is worded as "Do you have any demerit points?", so the answers
  // are inverted against the API's offenceFree flag: "No" (no demerit points)
  // means the driver is eligible for the offence-free discount => 'Y'.
  // Default to "No" while the field is shown; blank the answer once it is
  // hidden (NCD <= 20), as it is not asked at those NCD levels.
  useEffect(() => {
    if (!showOffenceFreeField) {
      offenceFree.setValue('');
    } else if (!offenceFree.value) {
      offenceFree.setValue('Y');
    }
  }, [offenceFree, showOffenceFreeField]);

  return (
    <div className="flex flex-col gap-4">
      <TabsCheckbox
        options={translatedGenderOptions}
        onChange={handleGenderChange}
        value={driver.value.gender}
        label={staticText.gender}
      />
      <TabsCheckbox
        options={translatedMaritalStatOptions}
        onChange={handleMaritalStatusChange}
        value={driver.value?.maritalStatus}
        label={staticText.marital_status}
      />
      {useBolttechData && (
        <>
          {translatedOccupationType && (
            <TabsCheckbox
              options={translatedOccupationType}
              onChange={handleOccupationTypeChange}
              value={driver.value?.occupationType}
              label={staticText.occupation_type || ''}
            />
          )}
          {translatedyesNoOptions && (
            <TabsCheckbox
              options={translatedyesNoOptions}
              onChange={handleOffPeakVehicleChange}
              value={offPeakVehicle.value}
              label={staticText.off_peak_vehicle || ''}
            />
          )}
        </>
      )}
      <FormSelect
        options={localization.form.driverAgeOptions}
        label={staticText.age}
        value={driver.value.age?.toString()}
        onChange={handleAgeChange}
      />
      <FormSelect
        options={
          useBolttechData && translatedBolttechDrivingExperience
            ? translatedBolttechDrivingExperience
            : translatedDrivingExperience
        }
        label={staticText.driving_experience}
        value={driver.value.totalDrivingExperience?.toString()}
        onChange={handleDrivingExperienceChange}
      />
      <FormSelect
        options={ncdOptions}
        label={staticText.ncd}
        value={ncd.value?.toString()}
        onChange={handleNCDChange}
      />
      {showOffenceFreeField && (
        <RadioButtonGroup
          defaultCheckedOption={offenceFree.value || 'Y'}
          helpText={
            <FeLink
              className="font-medium text-primary-800 text-base"
              href="https://eservices1.police.gov.sg/phub/eservices/landingpage/certificate-of-merit"
              target="_blank"
              rel="noreferrer"
            >
              {staticText.helpText}
            </FeLink>
          }
          options={localization.form.offfenceFreeOptions}
          onClick={handleOffenceFreeChange}
          label={staticText.offence_free || ''}
          name="offenceFree"
        />
      )}
      <FormSelect
        value={driver.value.numberOfClaims?.toString()}
        options={
          useBolttechData && bolttechNumberOfClaims
            ? bolttechNumberOfClaims
            : numberOfClaims
        }
        label={staticText.number_of_claims}
        onChange={handleNoOfClaimsChange}
      />
    </div>
  );
};
